import React, { useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import { Store__AtsStats } from "@talentbait/shared-components";

import { FlexWrapper, RandomizeButton } from "../../AdAssistant.styled";
import { RandomizeIcon } from "../../../../Components/Icons";
import { fetchRecommendationStream, getRandomElement } from "../../utils";
import { Header, Text, TextDescription, TextEditorBox } from "./TextEdition.styled";
import { LoadingWrapper } from "../../chat-bot/Chatbot.styled";
import LoadingIndicator from "react-loading-indicator";
import Actions from "../../../../Actions";
import AdAssistantStore from "../../AdAssistantStore";
import { withRefluxStore } from "../../../../Stores";
import StreamedTextFormatter from "../../streamed-text-formatter/StreamedTextFormatter";

import { useUpdateStoreMediaAssetsCurrentAd } from "../../hooks/useUpdateStoreMediaAssetsCurrentAd";
import { useUpdateStoreCurrentAd } from "../../hooks/useUpdateStoreCurrentAd";

const TextEdition = ({
  saveNewData,
  saveNewInputProps,
  // Store
  campaignData,
  currentAd,
  jobTitles,
  organization,
  userProfile,
  atsStats
}) => {
  const { t } = useTranslation();
  const updateStoreMediaAssetsCurrentAd = useUpdateStoreMediaAssetsCurrentAd(currentAd);
  const updateStoreCurrentAd = useUpdateStoreCurrentAd(currentAd);

  const [newJobTitle, setNewJobTitle] = useState(
    currentAd?.mediaAssets?.vertical?.inputProps?.text
  );
  const [streamedText, setStreamedText] = useState(currentAd?.text?.long || "");
  const [loading, setLoading] = useState(false);
  const [newJobTitleChanged, setNewJobTitleChanged] = useState(false);
  const [streamedTextChanged, setStreamedTextChanged] = useState(false);

  const handleSaveNewInputProps = ({ newInputs, showNotification }) => {
    saveNewInputProps(newInputs, showNotification);
  };

  const handleSaveNewData = ({ newData }) => {
    setStreamedTextChanged(false);
    saveNewData(newData);
  };

  const randomizeAndFetch = () => {
    Actions.setText("");
    fetchRecommendationStream({
      setLoading,
      setStreamedText,
      organization,
      userProfile,
      campaignData,
      newJobTitle,
      Actions,
      formality: atsStats?.formality
    }).then((streamedText) => {
      const { teaserText } = streamedText;
      setStreamedTextChanged(true);
      handleSaveNewData({ newData: teaserText });
    });
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setNewJobTitle(value);
    setNewJobTitleChanged(true);

    updateStoreMediaAssetsCurrentAd({ text: value });
  };

  const randomizeNewJobTitle = () => {
    const newTempJobTitle = getRandomElement(jobTitles);
    setNewJobTitle(newTempJobTitle);
    setNewJobTitleChanged(false);

    handleSaveNewInputProps({
      newInputs: [{ key: "text", value: newTempJobTitle }],
      showNotification: true
    });
  };

  const handleSave = () => {
    if (newJobTitleChanged) {
      setNewJobTitleChanged(false);
      const newInputs = [{ key: "text", value: newJobTitle }];
      handleSaveNewInputProps({ newInputs, showNotification: true });
    }

    if (streamedTextChanged) {
      setStreamedTextChanged(false);
      handleSaveNewData({ newData: streamedText });
    }
  };

  const handleInputPropsOnBlurSave = () => {
    if (newJobTitleChanged) {
      setNewJobTitleChanged(false);
      handleSaveNewInputProps({
        newInputs: [{ key: "text", value: newJobTitle }],
        showNotification: true
      });
    }
  };

  const handleTextDescriptionOnBlurSave = () => {
    if (streamedTextChanged) {
      setStreamedTextChanged(false);
      handleSaveNewData({ newData: streamedText });
    }
  };

  const handleTextDescriptionChange = (e) => {
    setStreamedText(e.target.value);
    setStreamedTextChanged(true);

    updateStoreCurrentAd({ text: { long: e.target.value, short: e.target.value } });
  };

  return (
    <FlexWrapper gap={15} direction={"column"} align={"flex-start"}>
      <TextEditorBox>
        <Header>{t("Headline")}</Header>
        <Text
          type="text"
          id="text-editing-assistant"
          value={newJobTitle}
          onChange={handleTextChange}
          onBlur={handleInputPropsOnBlurSave}
        />
        <RandomizeButton onClick={randomizeNewJobTitle}>
          <RandomizeIcon />
          {t("Randomize")}
        </RandomizeButton>
      </TextEditorBox>
      <TextEditorBox>
        <Header>{t("Post text")}</Header>
        {loading ? (
          <FlexWrapper direction="column" gap={10} align="center">
            <Text>{t("TalentBait AI generating text...")}</Text>
            <LoadingWrapper>
              <LoadingIndicator />
            </LoadingWrapper>
          </FlexWrapper>
        ) : (
          <TextDescription
            value={streamedText}
            onChange={handleTextDescriptionChange}
            onBlur={handleTextDescriptionOnBlurSave}
          >
            <AnimatePresence>
              <StreamedTextFormatter text={streamedText} />
            </AnimatePresence>
          </TextDescription>
        )}
        <RandomizeButton onClick={randomizeAndFetch}>
          <RandomizeIcon />
          {t("Randomize")}
        </RandomizeButton>
      </TextEditorBox>
    </FlexWrapper>
  );
};

export default memo(
  withRefluxStore(
    TextEdition,
    [AdAssistantStore, Store__AtsStats],
    ["organization", "userProfile", "campaignData", "jobTitles", "text", "currentAd", "atsStats"]
  )
);
